import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, FormControl } from '@angular/forms';
import {
    SharedModule,
    StringCustomFieldConfig,
    FormInputComponent,
    NotificationService,
    DynamicFormInputComponent
} from '@vendure/admin-ui/core';
import { notNullOrUndefined } from '@vendure/common/lib/shared-utils';
import { BehaviorSubject } from 'rxjs';
import { OptionDsisplayType } from '../../common/ui/ui-types';
import { PaidOptionData, PaidOptionItem } from '../common/ui-types';
import { safeJSONParse, isEqual } from '../../common/ui/utils';

@Component({
    selector: 'vdr-paid-options-form-input',
    template: `
    <vdr-card>
        <div class="form-row">
            <vdr-form-field [label]="'Required'" class="form-item">
                <clr-toggle-wrapper>
                    <input
                        type="checkbox"
                        clrToggle
                        name="required"
                        [formControl]="requiredControl"
                    />
                </clr-toggle-wrapper>
            </vdr-form-field>
            <vdr-form-field [label]="'Show Image'" class="form-item">
                <clr-toggle-wrapper>
                    <input
                        type="checkbox"
                        clrToggle
                        name="showImage"
                        [formControl]="showImageControl"
                    />
                </clr-toggle-wrapper>
            </vdr-form-field>
        </div>
        <div class="form-row">
          <vdr-form-field label="Display Type" for="displayType">
            <select id="displayType" [formControl]="displayTypeControl">
              <option *ngIf="showImageControl.value" value="image-tiles">Image Tiles</option>
              <option value="drop-down-list">Drop Down List</option>
              <option value="option-list">Option List</option>
            </select>
          </vdr-form-field>
        </div>
        <div class="form-row">
            <vdr-form-field [label]="'Label'" class="form-item">
                <input type="text" [formControl]="labelControl">
            </vdr-form-field>
        </div>
        <div class="form-row">
            <vdr-form-field [label]="'Description'" class="form-item">
                <vdr-rich-text-editor [formControl]="descriptionControl" class="form-control">
                </vdr-rich-text-editor>
            </vdr-form-field>
        </div>
        <div class="form-row">
            <vdr-form-field [label]="'Options'" class="form-item">
                <vdr-dynamic-form-input
                    #optionsInput
                    [control]="optionsControl"
                    [def]="optionItemDef"
                >
                </vdr-dynamic-form-input>
            </vdr-form-field>
        </div>
    </vdr-card>
    `,
    styleUrls: ['../../common/ui/styles.scss'],
    standalone: true,
    imports: [SharedModule],
})
export class PaidOptionsFormInputComponent
    implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit {
    @ViewChild('optionsInput') optionsInput: DynamicFormInputComponent;
    readonly: boolean;
    config: StringCustomFieldConfig;
    formControl: FormControl;

    labelControl = new FormControl();
    descriptionControl = new FormControl();
    requiredControl = new FormControl();
    showImageControl = new FormControl();
    displayTypeControl = new FormControl();
    optionsControl = new UntypedFormControl();
    optionItemDef: any;
    private showImage$ = new BehaviorSubject<boolean>(false);
    paidOptionsData: PaidOptionData;

    constructor(
        private notificationService: NotificationService,
        private  changeDetector: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        const defaultPaidOptionData: PaidOptionData = {
            label: '',
            description: '',
            required: false,
            showImage: false,
            displayType: OptionDsisplayType.DropDownList,
            options: []
        };
        this.paidOptionsData = safeJSONParse<PaidOptionData>(
            this.formControl.value,
            this.notificationService,
            defaultPaidOptionData,
        )!;
        // Normalize legacy option items that may have been stored as JSON strings
        if (this.paidOptionsData?.options) {
            this.paidOptionsData.options = this.paidOptionsData.options.map((o: any) => {
                if (typeof o === 'string') {
                    return safeJSONParse<PaidOptionItem>(o, this.notificationService) ?? null;
                }
                return o;
            }).filter(notNullOrUndefined) ?? [];
        }
        this.initializeFormControls();
        this.showImage$.next(this.paidOptionsData?.showImage || false);
        this.optionItemDef = {
            type: 'string',
            list: true,
            ui: {
                component: 'paid-customization-item-form',
                options: { showImage$: this.showImage$ }
            }
        };
    }

    ngAfterViewInit() {
        this.labelControl.valueChanges.subscribe(this.onLabelChange);
        this.descriptionControl.valueChanges.subscribe(this.onDescriptionChange);
        this.requiredControl.valueChanges.subscribe(this.onRequiredChange);
        this.showImageControl.valueChanges.subscribe(this.onShowImageChange);
        this.displayTypeControl.valueChanges.subscribe(this.onDisplayTypeChange);
        if(this.optionsInput) {
            this.optionsInput.registerOnChange(this.onOptionsChange);
            // DynamicFormInputComponent with type:'string' expects string[] — serialize each PaidOptionItem
            const optionsAsStrings = (this.paidOptionsData?.options || []).map(o => JSON.stringify(o));
            this.optionsInput.writeValue(optionsAsStrings);
        }
    }

    private initializeFormControls() {
        this.labelControl.setValue(this.paidOptionsData?.label);
        this.descriptionControl.setValue(this.paidOptionsData?.description);
        this.requiredControl.setValue(this.paidOptionsData?.required);
        this.showImageControl.setValue(this.paidOptionsData?.showImage);
        this.displayTypeControl.setValue(this.paidOptionsData?.displayType);
        this.optionsControl.setValue(this.paidOptionsData?.options);
    }

    private onLabelChange = (value: string) => {
        if (this.paidOptionsData?.label !== value) {
            this.paidOptionsData!.label = value;
            this.updateFormValue();
        }
    };

    private onDescriptionChange = (value: string) => {
        if (this.paidOptionsData?.description !== value) {
            this.paidOptionsData!.description = value;
            this.updateFormValue();
        }
    };

    private onRequiredChange = (value: boolean) => {
        if (this.paidOptionsData?.required !== value) {
            this.paidOptionsData!.required = value;
            this.updateFormValue();
        }
    };

    private onShowImageChange = (val: boolean) => {
        if (this.paidOptionsData?.showImage !== val) {
            this.paidOptionsData!.showImage = val;
            this.showImage$.next(val);
            if (!val && this.displayTypeControl.value === OptionDsisplayType.ImageTiles) {
                this.displayTypeControl.setValue( OptionDsisplayType.DropDownList );
            }
            this.updateFormValue();
            this.changeDetector.markForCheck();
        }
    };

    private onDisplayTypeChange = (val: OptionDsisplayType) => {
        if (this.paidOptionsData?.displayType !== val) {
            this.paidOptionsData!.displayType = val;
            this.updateFormValue();
        }
    };

    private onOptionsChange = (value: string[]) => {
        // DynamicFormInputComponent passes strings — parse each back to PaidOptionItem
        const parsed = value.map(s => { try { return JSON.parse(s); } catch { return s; } });
        if (!isEqual(this.paidOptionsData?.options, parsed)) {
            this.paidOptionsData!.options = parsed;
            this.updateFormValue();
        }
    };

    private updateFormValue() {
        this.formControl.setValue(this.paidOptionsData);
        this.formControl.markAsDirty();
    }
}